import { NavigateOptions, To, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Bin } from 'app/modules/inventory/bins/core/type';
import { PRIORITY_EXPIRY_STATUS } from 'app/modules/inventory/racks/core/constants';
import { IBatchExpiryStatus } from 'app/modules/inventory/racks/core/enum';
import { getFormatedDate } from 'app/modules/reporting/core/helpers';

import { blockInvalidCharacters } from 'app/helpers';
import { cloneDeep, get, has, isArray, partial, some } from 'lodash';

import { PUBLIC_PATHS } from 'app/constants/path';

import { stringify } from 'qs';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

export const isPublicPath = (path: string) =>
  PUBLIC_PATHS.some((publicPath) => path.startsWith(publicPath));

export const QUERY_KEY = {
  filter: {
    ward_id: 'ward_id',
    ward_name: 'ward_name',
    from_complete_date: 'from_complete_date',
    to_complete_date: 'to_complete_date',
    status: 'status',
    to_commence_date: 'to_commence_date',
    from_commence_date: 'from_commence_date',
  },
  table: {
    page: 'page',
    limit: 'limit',
    orderBy: 'orderBy',
    orderDirection: 'orderDirection',
  },
  viewMode: 'viewMode',
};

export const injectDragToScroll = (elementId: string) => {
  const container: any = document.querySelector(`#${elementId}`);
  if (!container) return;
  let startY;
  let startX;
  let scrollLeft;
  let scrollTop;
  let isDown;

  function mouseIsDown(e) {
    if (e.target.id.includes('bin-drag-')) return;
    isDown = true;
    startY = e.pageY - container.offsetTop;
    startX = e.pageX - container.offsetLeft;
    scrollLeft = container.scrollLeft;
    scrollTop = container.scrollTop;
  }
  function mouseUp() {
    isDown = false;
  }
  function mouseLeave() {
    isDown = false;
  }
  function mouseMove(e) {
    if (isDown) {
      e.preventDefault();
      //Move vertcally
      const y = e.pageY - container.offsetTop;
      const walkY = y - startY;
      container.scrollTop = scrollTop - walkY;

      //Move Horizontally
      const x = e.pageX - container.offsetLeft;
      const walkX = x - startX;
      container.scrollLeft = scrollLeft - walkX;
    }
  }

  container.addEventListener('mousedown', (e) => mouseIsDown(e));
  container.addEventListener('mousemove', (e) => mouseMove(e));
  container.addEventListener('mouseup', mouseUp);
  container.addEventListener('mouseleave', mouseLeave);
};

export const useGoBackIfExist = (defaultPath: string) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  const doesAnyHistoryEntryExist = location.key !== 'default';

  return () => {
    if (doesAnyHistoryEntryExist) {
      navigate(-1);
      return;
    }

    // Handle default navigation
    const currentOrgId = orgId || organizationId;
    if (currentOrgId && !defaultPath.includes('/org/')) {
      navigate(`/org/${currentOrgId}${defaultPath}`);
      return;
    }

    navigate(defaultPath);
  };
};

export const usePageGoBack = (defaultPath?: string) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return () => {
    const state = location.state as { previousPath?: string };
    const currentOrgId = orgId || organizationId;

    // Handle previous path navigation
    if (state?.previousPath) {
      const previousPath = state.previousPath;

      if (currentOrgId && !previousPath.includes('/org/') && !previousPath.startsWith('/login')) {
        navigate(`/org/${currentOrgId}${previousPath}`);
        return;
      }

      navigate(previousPath);
      return;
    }

    // Handle default path navigation
    if (defaultPath) {
      if (currentOrgId && !defaultPath.includes('/org/')) {
        navigate(`/org/${currentOrgId}${defaultPath}`);
        return;
      }

      navigate(defaultPath);
      return;
    }

    // Fallback navigation
    navigate(-1 as To);
  };
};

export const useNavigateWithSendPath = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams();
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return (to: To, options?: NavigateOptions) => {
    if (typeof to === 'string' && to.includes('/org/')) {
      navigate(to, {
        ...options,
        state: {
          ...options?.state,
          previousPath: `${location.pathname}${location.search}`,
        },
      });
      return;
    }

    // Get the current organization ID from the URL or from the store
    const currentOrgId = orgId || organizationId;

    // Add the organization prefix to the path if needed
    let targetPath: To = to;
    if (typeof to === 'string' && to.startsWith('/') && currentOrgId && !isPublicPath(to)) {
      targetPath = `/org/${currentOrgId}${to}`;
    }

    navigate(targetPath, {
      ...options,
      state: {
        ...options?.state,
        previousPath: `${location.pathname}${location.search}`,
      },
    });
  };
};

export const useNavigateWithPreviousPath = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams();
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return (to: To, options?: NavigateOptions) => {
    const locationState = location.state as { previousPath?: string };

    // If the path already includes /org/, don't modify it
    if (typeof to === 'string' && to.includes('/org/')) {
      navigate(to, {
        ...options,
        state: {
          ...options?.state,
          previousPath: locationState?.previousPath,
        },
      });
      return;
    }

    // Get the current organization ID from the URL or from the store
    const currentOrgId = orgId || organizationId;

    // Add the organization prefix to the path if needed
    let targetPath: To = to;
    if (typeof to === 'string' && to.startsWith('/') && currentOrgId && !isPublicPath(to)) {
      targetPath = `/org/${currentOrgId}${to}`;
    }

    navigate(targetPath, {
      ...options,
      state: {
        ...options?.state,
        previousPath: locationState?.previousPath,
      },
    });
  };
};

export const filterBySearch = (items: any[], search: string, searchKeys: string[]) => {
  // Escape special characters that have meaning in regular expressions
  const test = search.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
  const regex = new RegExp(test ?? '', 'gi');

  return items.filter((el) =>
    searchKeys.some((key) => {
      const value = get(el, key);
      return value && value.search(regex) >= 0;
    }),
  );
};

export const haveKeysIn = (obj: any, requiredKeys: string[]) =>
  some(requiredKeys, partial(has, obj));

export const stringifyObject = (obj: any) => {
  const newObj = cloneDeep(obj);
  Object.entries(obj).forEach(([key, value]) => {
    const valueStringified = typeof value === 'object' ? JSON.stringify(value) : String(value);
    newObj[key] = isArray(value) ? value : valueStringified;
  });
  return newObj;
};

export const getPathWithParams = (path: string, params: object) => {
  return `${path}?${stringify(params)}`;
};

/**
 * Determines the highest priority status that exists in the expiry list.
 * The priority order is: expired > warning > normal.
 *
 * @param expiryList - The list of expiry items to check.
 * @returns The highest priority IBatchExpiryStatus found in the expiry list, or IBatchExpiryStatus.NORMAL if none are found.
 */
export const representativeExpiryStatus = (expiryList: Bin['expiry']): IBatchExpiryStatus =>
  PRIORITY_EXPIRY_STATUS.find((status) =>
    (expiryList ?? []).some((item) => item.status === status),
  ) || IBatchExpiryStatus.NORMAL;

/**
 * Generates a random color from a predefined set of colors.
 *
 * @returns {string} A random color as a string.
 */
export const generateRandomColor = (): string => {
  const colors = [
    'var(--primary)',
    'var(--success-3)',
    'var(--warning)',
    'var(--info)',
    'var(--teal)',
    'var(--pink)',
  ];

  const randomIndex = Math.floor(Math.random() * colors.length);
  return colors[randomIndex];
};

/**
 * Extracts the first two initials from a given name.
 *
 * @param {string} name - The name from which to extract initials.
 * @returns {string} The first two initials of the name in uppercase.
 */
export const getInitialsCharacter = (name: string): string =>
  name
    .split(' ') // Split into words
    .map((word) => word.charAt(0).toUpperCase()) // Get first letter
    .slice(0, 2) // Keep only the first two initials
    .join('');

export const downloadFile = (url: string, filename: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadImage = (url: string, filename = 'image') => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'image.jpg';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadZip = (data: any) => {
  // Create a blob URL from the response data
  const blob = new Blob([data], { type: 'application/zip' });
  const url = window.URL.createObjectURL(blob);

  // Create a temporary link element to trigger the download
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `Assets-${getFormatedDate(new Date())}.zip`);
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const handleBlockInvalidCharacters = (event: React.KeyboardEvent<HTMLInputElement>) =>
  blockInvalidCharacters(event, 'integer');
